import { clsx } from 'clsx';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { initials } from '@/lib/format';

export const cx = clsx;

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'icon' | 'icon-sm';

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean }
>(function Button({ variant = 'secondary', size = 'md', loading, className, children, disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors select-none whitespace-nowrap',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50 disabled:pointer-events-none',
        size === 'sm' && 'h-7 px-2.5 text-xs',
        size === 'md' && 'h-9 px-3.5 text-sm',
        size === 'icon' && 'h-9 w-9',
        size === 'icon-sm' && 'h-7 w-7',
        variant === 'primary' && 'bg-accent text-white hover:bg-accent-2',
        variant === 'secondary' && 'bg-panel-3 text-fg hover:bg-line border border-line',
        variant === 'ghost' && 'text-muted hover:text-fg hover:bg-panel-3',
        variant === 'danger' && 'bg-danger/90 text-white hover:bg-danger',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={14} /> : null}
      {children}
    </button>
  );
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cx(
        'h-9 w-full rounded-lg border border-line bg-panel-2 px-3 text-sm text-fg placeholder:text-faint',
        'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25',
        className,
      )}
      {...rest}
    />
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cx(
        'w-full rounded-lg border border-line bg-panel-2 px-3 py-2 text-sm text-fg placeholder:text-faint',
        'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25',
        className,
      )}
      {...rest}
    />
  );
});

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cx(
        'h-9 rounded-lg border border-line bg-panel-2 px-2.5 text-sm text-fg focus:border-accent focus:outline-none',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between">
      <span className="text-xs font-medium text-muted">{children}</span>
      {hint ? <span className="text-xs text-faint">{hint}</span> : null}
    </div>
  );
}

export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <Label hint={hint}>{label}</Label>
      {children}
    </label>
  );
}

export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <span
      className={cx('inline-block rounded-full border-2 border-current border-r-transparent', className)}
      style={{ width: size, height: size, animation: 'vm-spin 0.7s linear infinite' }}
    />
  );
}

export function Avatar({ name, url, size = 28, color }: { name: string; url?: string; size?: number; color?: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={name}
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size, boxShadow: color ? `0 0 0 2px ${color}` : undefined }}
      />
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-panel-3 font-semibold text-muted"
      style={{ width: size, height: size, fontSize: size * 0.38, boxShadow: color ? `0 0 0 2px ${color}` : undefined }}
    >
      {initials(name)}
    </span>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium',
        tone === 'neutral' && 'bg-panel-3 text-muted',
        tone === 'accent' && 'bg-accent/15 text-accent-2',
        tone === 'ok' && 'bg-ok/15 text-ok',
        tone === 'warn' && 'bg-warn/15 text-warn',
        tone === 'danger' && 'bg-danger/15 text-danger',
      )}
    >
      {children}
    </span>
  );
}

/**
 * For the outermost element of anything drawn in a portal (dialogs, menus,
 * popovers): React passes events from a portal on to the components it sits
 * in, so a right-click there would otherwise open the menu of what is behind.
 */
export const stopPortalContextMenu = (e: React.MouseEvent) => e.stopPropagation();

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 440,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]" onMouseDown={onClose} onContextMenu={stopPortalContextMenu}>
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[90vh] w-full overflow-auto rounded-2xl border border-line bg-panel shadow-2xl"
        style={{ maxWidth: width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
            <X size={16} />
          </Button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer ? <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  danger,
  requireText,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<unknown> | void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireText?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  useEffect(() => setText(''), [open]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            disabled={!!requireText && text !== requireText}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-muted">{message}</div>
      {requireText ? (
        <div className="mt-3">
          <Label>
            Type <b className="text-fg">{requireText}</b> to confirm
          </Label>
          <Input value={text} onChange={(e) => setText(e.target.value)} autoFocus />
        </div>
      ) : null}
    </Modal>
  );
}

export interface MenuItem {
  label: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/** Free-form content in a menu (e.g. a download button); clicking inside it closes the menu. */
export interface MenuContent {
  content: ReactNode;
}

export function Menu({ trigger, items, align = 'right' }: { trigger: ReactNode; items: (MenuItem | MenuContent | 'sep' | null)[]; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The panel renders in a portal with fixed positioning so containers with
  // overflow hidden (cards, scroll areas) cannot clip it. It opens upwards
  // when there is not enough room below (e.g. the account menu at the bottom of the sidebar).
  useLayoutEffect(() => {
    if (!open) return setPos(null);
    const t = ref.current?.getBoundingClientRect();
    const panel = panelRef.current;
    if (!t || !panel) return;
    const h = panel.offsetHeight;
    const w = panel.offsetWidth;
    const up = t.bottom + h + 8 > window.innerHeight && t.top - h - 8 > 0;
    const left = align === 'right' ? t.right - w : t.left;
    setPos({
      left: Math.max(8, Math.min(left, window.innerWidth - w - 8)),
      ...(up ? { bottom: window.innerHeight - t.top + 4 } : { top: t.bottom + 4 }),
    });
  }, [open, align]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    // A fixed panel would drift away from its trigger when the page scrolls.
    const close = (e: Event) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  return (
    <div className="relative inline-block" ref={ref}>
      <div onClick={(e) => (e.stopPropagation(), setOpen((o) => !o))}>{trigger}</div>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              onContextMenu={stopPortalContextMenu}
              className="fixed z-[70] min-w-[180px] overflow-hidden rounded-xl border border-line bg-panel-2 py-1 shadow-xl"
              style={pos ?? { top: 0, left: 0, visibility: 'hidden' }}
            >
              {items.map((it, i) =>
                it === null ? null : it === 'sep' ? (
                  <div key={i} className="my-1 border-t border-line" />
                ) : 'content' in it ? (
                  <div key={i} onClick={() => setOpen(false)}>
                    {it.content}
                  </div>
                ) : (
                  <button
                    key={i}
                    disabled={it.disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      it.onClick();
                    }}
                    className={cx(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-panel-3 disabled:opacity-40 [@media(pointer:coarse)]:py-2.5',
                      it.danger ? 'text-danger' : 'text-fg',
                    )}
                  >
                    {it.icon}
                    {it.label}
                  </button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line px-6 py-16 text-center">
      {icon ? <div className="mb-3 text-faint">{icon}</div> : null}
      <div className="text-[15px] font-semibold">{title}</div>
      {children ? <div className="mt-1 max-w-md text-sm text-muted">{children}</div> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { id: T; label: ReactNode }[] }) {
  return (
    <div className="flex gap-1 rounded-lg bg-panel-2 p-0.5">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={cx(
            'rounded-md px-2.5 py-1 text-xs font-medium',
            value === t.id ? 'bg-panel-3 text-fg shadow' : 'text-muted hover:text-fg',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Progress({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cx('h-1.5 w-full overflow-hidden rounded-full bg-panel-3', className)}>
      <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%` }} />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-lg font-semibold tracking-tight sm:text-xl">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Card({ children, className, ...rest }: { children: ReactNode; className?: string } & Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'children'>) {
  return (
    <div {...rest} className={cx('rounded-2xl border border-line bg-panel', className)}>
      {children}
    </div>
  );
}

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cx('inline-flex items-center gap-2 text-sm', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}>
      <span
        onClick={() => !disabled && onChange(!checked)}
        className={cx('relative h-5 w-9 rounded-full transition-colors', checked ? 'bg-accent' : 'bg-panel-3 border border-line')}
      >
        <span className={cx('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all', checked ? 'left-[18px]' : 'left-0.5')} />
      </span>
      {label}
    </label>
  );
}
